import { SettingsRow, SettingsSection, Switch } from '@wundo/ui';
import type { MeetingFollowPreferences } from '@/lib/meetingFollow.ts';
import { m } from '@/paraglide/messages.js';

interface MeetingKindsSectionProps {
  preferences: MeetingFollowPreferences;
  onChange: (patch: Partial<MeetingFollowPreferences>) => void;
}

/** Which kinds of meeting Wundo follows: one-to-ones, team meetings, or all of them. */
export function MeetingKindsSection({ preferences, onChange }: MeetingKindsSectionProps) {
  /*
   * "All conversations" covers both kinds, so it turns them on and locks them
   * rather than sitting beside them: two switches that change nothing while it
   * is on would be a choice that is not one. Turning it off turns everything
   * off — it is one setting, not a layer over the other two.
   */
  const changeFollowAll = (checked: boolean) =>
    onChange({
      follow_all: checked,
      follow_one_on_ones: checked,
      follow_team_meetings: checked,
    });

  const kindsLocked = preferences.follow_all;

  return (
    <SettingsSection
      title={m.meetings_kinds_title()}
      description={m.meetings_kinds_desc()}
    >
      <SettingsRow
        label={m.meetings_kind_one_on_ones()}
        description={
          kindsLocked
            ? m.meetings_kind_covered_by_all()
            : m.meetings_kind_one_on_ones_desc()
        }
      >
        <Switch
          aria-label={m.meetings_kind_one_on_ones()}
          disabled={kindsLocked}
          checked={preferences.follow_one_on_ones}
          onCheckedChange={(checked) => onChange({ follow_one_on_ones: checked })}
        />
      </SettingsRow>
      <SettingsRow
        label={m.meetings_kind_team()}
        description={
          kindsLocked ? m.meetings_kind_covered_by_all() : m.meetings_kind_team_desc()
        }
      >
        <Switch
          aria-label={m.meetings_kind_team()}
          disabled={kindsLocked}
          checked={preferences.follow_team_meetings}
          onCheckedChange={(checked) => onChange({ follow_team_meetings: checked })}
        />
      </SettingsRow>
      <SettingsRow label={m.meetings_kind_all()} description={m.meetings_kind_all_desc()}>
        <Switch
          aria-label={m.meetings_kind_all()}
          checked={preferences.follow_all}
          onCheckedChange={changeFollowAll}
        />
      </SettingsRow>
    </SettingsSection>
  );
}
